import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const LifecyclePage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('onboarding');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showOffboardingModal, setShowOffboardingModal] = useState(false);
  const [offboardingForm, setOffboardingForm] = useState({
    termination_date: '',
    reason: ''
  });
  const [employeeAssets, setEmployeeAssets] = useState({ equipment: [], vehicles: [] });
  const [loadingAssets, setLoadingAssets] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeAssets = async (employeeId) => {
    setLoadingAssets(true);
    try {
      const [eqRes, vehRes] = await Promise.all([
        api.get(`/hr/equipment/employee/${employeeId}`),
        api.get(`/hr/vehicles/employee/${employeeId}`)
      ]);
      setEmployeeAssets({
        equipment: eqRes.data || [],
        vehicles: vehRes.data || []
      });
    } catch (error) {
      console.error('Error fetching assets:', error);
      setEmployeeAssets({ equipment: [], vehicles: [] });
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleStartOnboarding = async (employeeId) => {
    try {
      await api.post(`/hr/lifecycle/${employeeId}/start-onboarding`);
      toast.success('Onboarding iniciado com sucesso');
      fetchEmployees();
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar onboarding');
    }
  };

  const openOffboardingModal = (employee) => {
    setSelectedEmployee(employee);
    setShowOffboardingModal(true);
    fetchEmployeeAssets(employee.employee_id);
  };

  const handleStartOffboarding = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    try {
      await api.post(`/hr/lifecycle/${selectedEmployee.employee_id}/start-offboarding`, {
        termination_date: offboardingForm.termination_date,
        reason: offboardingForm.reason
      });
      toast.success('Offboarding iniciado com sucesso');
      setShowOffboardingModal(false);
      setSelectedEmployee(null);
      setOffboardingForm({ termination_date: '', reason: '' });
      setEmployeeAssets({ equipment: [], vehicles: [] });
      fetchEmployees();
    } catch (error) {
      console.error('Error starting offboarding:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar offboarding');
    }
  };

  const handleUpdateChecklist = async (employeeId, checklistType, stepId, completed) => {
    try {
      await api.put(`/hr/lifecycle/${employeeId}/checklist/${stepId}?process_type=${checklistType}&completed=${completed}`);
      toast.success('Checklist atualizado');
      fetchEmployees();
    } catch (error) {
      console.error('Error updating checklist:', error);
      toast.error('Erro ao atualizar checklist');
    }
  };

  const onboardingEmployees = employees.filter(e => 
    e.onboarding_status === 'pending' || e.onboarding_status === 'in_progress'
  );
  
  const offboardingEmployees = employees.filter(e => 
    e.offboarding_status === 'in_progress'
  );

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      pending: 'Pendente',
      in_progress: 'Em Curso',
      completed: 'Concluído'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      IT: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      it: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      HR: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      hr: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      manager: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      Training: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      Facilities: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      Team: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    };
    return icons[category] || icons.hr;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="lifecycle-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Lifecycle Management</h1>
          <p className="text-muted-foreground">Onboarding e Offboarding de colaboradores</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{onboardingEmployees.length}</p>
              <p className="text-xs text-muted-foreground">Em Onboarding</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{offboardingEmployees.length}</p>
              <p className="text-xs text-muted-foreground">Em Offboarding</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{employees.filter(e => e.onboarding_status === 'completed').length}</p>
              <p className="text-xs text-muted-foreground">Onboarding Completo</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900/30">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{employees.filter(e => e.status === 'active').length}</p>
              <p className="text-xs text-muted-foreground">Colaboradores Ativos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('onboarding')}
          className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'onboarding' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Onboarding ({onboardingEmployees.length})
        </button>
        <button
          onClick={() => setActiveTab('offboarding')}
          className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'offboarding' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Offboarding ({offboardingEmployees.length})
        </button>
        <button
          onClick={() => setActiveTab('start')}
          className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'start' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Iniciar Processo
        </button>
      </div>

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <div className="space-y-4">
          {onboardingEmployees.length === 0 ? (
            <div className="rounded-xl border bg-card p-8 text-center">
              <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <p className="text-muted-foreground">Sem processos de onboarding em curso</p>
            </div>
          ) : (
            onboardingEmployees.map((employee) => (
              <div key={employee.employee_id} className="rounded-xl border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <span className="text-lg font-semibold text-blue-600">{employee.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold">{employee.name}</h3>
                      <p className="text-sm text-muted-foreground">{employee.position} - {employee.department}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(employee.onboarding_status)}
                    <button
                      onClick={() => navigate(`/employees/${employee.employee_id}`)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Checklist */}
                <div className="space-y-2">
                  {(employee.onboarding_checklist || []).map((item, index) => (
                    <div key={item.step_id || index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <input
                        type="checkbox"
                        checked={item.completed}
                        onChange={(e) => handleUpdateChecklist(employee.employee_id, 'onboarding', item.step_id, e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <div className="p-1 rounded bg-muted">
                          {getCategoryIcon(item.responsible || item.category)}
                        </div>
                        <span className={item.completed ? 'line-through text-muted-foreground' : ''}>{item.title || item.task}</span>
                      </div>
                      <span className="text-xs px-2 py-1 rounded bg-muted">{item.responsible || item.category}</span>
                    </div>
                  ))}
                </div>

                {/* Progress */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progresso</span>
                    <span>
                      {(employee.onboarding_checklist || []).filter(i => i.completed).length} / {(employee.onboarding_checklist || []).length}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ 
                        width: `${((employee.onboarding_checklist || []).filter(i => i.completed).length / (employee.onboarding_checklist || []).length) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Offboarding Tab */}
      {activeTab === 'offboarding' && (
        <div className="space-y-4">
          {offboardingEmployees.length === 0 ? (
            <div className="rounded-xl border bg-card p-8 text-center">
              <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
              </svg>
              <p className="text-muted-foreground">Sem processos de offboarding em curso</p>
            </div>
          ) : (
            offboardingEmployees.map((employee) => (
              <div key={employee.employee_id} className="rounded-xl border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <span className="text-lg font-semibold text-amber-600">{employee.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold">{employee.name}</h3>
                      <p className="text-sm text-muted-foreground">{employee.position} - {employee.department}</p>
                      {employee.termination_date && (
                        <p className="text-sm text-red-600">Data de saída: {employee.termination_date}</p>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(employee.offboarding_status)}
                </div>

                {/* Checklist */}
                <div className="space-y-2">
                  {(employee.offboarding_checklist || []).map((item, index) => (
                    <div key={item.step_id || index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <input
                        type="checkbox"
                        checked={item.completed}
                        onChange={(e) => handleUpdateChecklist(employee.employee_id, 'offboarding', item.step_id, e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <div className="p-1 rounded bg-muted">
                          {getCategoryIcon(item.responsible || item.category)}
                        </div>
                        <span className={item.completed ? 'line-through text-muted-foreground' : ''}>{item.title || item.task}</span>
                      </div>
                      <span className="text-xs px-2 py-1 rounded bg-muted">{item.responsible || item.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Start Process Tab */}
      {activeTab === 'start' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Start Onboarding */}
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Iniciar Onboarding
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Selecione um colaborador para iniciar o processo de onboarding.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {employees
                .filter(e => e.status === 'active' && (!e.onboarding_status || e.onboarding_status === 'pending'))
                .map((employee) => (
                  <div key={employee.employee_id} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <div>
                      <p className="font-medium">{employee.name}</p>
                      <p className="text-sm text-muted-foreground">{employee.position}</p>
                    </div>
                    <button
                      onClick={() => handleStartOnboarding(employee.employee_id)}
                      className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-medium transition-colors"
                    >
                      Iniciar
                    </button>
                  </div>
                ))}
              {employees.filter(e => e.status === 'active' && (!e.onboarding_status || e.onboarding_status === 'pending')).length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Todos os colaboradores já têm onboarding iniciado ou concluído
                </p>
              )}
            </div>
          </div>

          {/* Start Offboarding */}
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
              </svg>
              Iniciar Offboarding
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Selecione um colaborador para iniciar o processo de offboarding.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {employees
                .filter(e => e.status === 'active' && !e.offboarding_status)
                .map((employee) => (
                  <div key={employee.employee_id} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <div>
                      <p className="font-medium">{employee.name}</p>
                      <p className="text-sm text-muted-foreground">{employee.position}</p>
                    </div>
                    <button
                      onClick={() => openOffboardingModal(employee)}
                      className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 text-sm font-medium transition-colors"
                    >
                      Iniciar
                    </button>
                  </div>
                ))}
              {employees.filter(e => e.status === 'active' && !e.offboarding_status).length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Sem colaboradores disponíveis para offboarding
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offboarding Modal */}
      {showOffboardingModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Iniciar Offboarding</h2>
              <p className="text-sm text-muted-foreground">Colaborador: {selectedEmployee.name}</p>
            </div>
            <form onSubmit={handleStartOffboarding} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Data de Saída</label>
                <input
                  type="date"
                  value={offboardingForm.termination_date}
                  onChange={(e) => setOffboardingForm({ ...offboardingForm, termination_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivo</label>
                <textarea
                  value={offboardingForm.reason}
                  onChange={(e) => setOffboardingForm({ ...offboardingForm, reason: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={3}
                  placeholder="Motivo da saída (opcional)"
                />
              </div>

              {/* Assets Section */}
              <div className="border-t border-border pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Ativos a Recolher
                </h4>
                {loadingAssets ? (
                  <div className="animate-pulse bg-muted h-16 rounded-lg"></div>
                ) : (
                  <div className="space-y-3">
                    {(employeeAssets.equipment.length === 0 && employeeAssets.vehicles.length === 0) ? (
                      <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                        Nenhum equipamento ou viatura atribuída a este colaborador.
                      </p>
                    ) : (
                      <>
                        {employeeAssets.equipment.length > 0 && (
                          <div className="bg-muted/50 p-3 rounded-lg">
                            <p className="text-xs font-medium text-muted-foreground mb-2">EQUIPAMENTOS ({employeeAssets.equipment.length})</p>
                            <div className="space-y-1">
                              {employeeAssets.equipment.map((eq) => (
                                <div key={eq.equipment_id} className="flex items-center justify-between text-sm">
                                  <span className="font-medium">{eq.brand} {eq.model}</span>
                                  <span className="text-muted-foreground">SN: {eq.serial_number || 'N/A'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {employeeAssets.vehicles.length > 0 && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2">VIATURAS ({employeeAssets.vehicles.length})</p>
                            <div className="space-y-1">
                              {employeeAssets.vehicles.map((veh) => (
                                <div key={veh.vehicle_id} className="flex items-center justify-between text-sm">
                                  <span className="font-medium">{veh.license_plate}</span>
                                  <span className="text-muted-foreground">{veh.brand} {veh.model}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                          Estes ativos devem ser recolhidos e devolvidos durante o processo de offboarding.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowOffboardingModal(false);
                    setSelectedEmployee(null);
                    setEmployeeAssets({ equipment: [], vehicles: [] });
                  }}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                >
                  Iniciar Offboarding
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifecyclePage;
